import {useMemo, useRef} from 'react';
import {Typography} from '@mui/material';
import {t} from 'i18next';
import AnimatedProgressSingleStep from './component/AnimatedProgressSingleStep';
import AurTooltip from '../aur-tooltip/AurTooltip';
import {ReactComponent as InfoIcon} from '../../../assests/svg/info_icon.svg';
import {useWindowSize} from '../../../hooks/utils/useWindowSize';
import {getUTCDateTimeForDisplay} from '../../../utils/utils';
import {AurLightTooltip} from '../aur-light-tooltip/aur-light-tooltip';
import {
  ONLY_DATE_SHOW_FORMAT,
  ONLY_TIME_SHOW_FORMAT,
} from '../../../constants/constants';

// Individual single step's current processing state
export type STEP_PROCESSING_STATUS =
  | 'NotStarted'
  | 'InProgress'
  | 'Completed'
  | 'Failed'
  | 'Missing';

// This use to map Progress Status Type to particular key which is define in string file for show caption and error string
export const ReportStatusCaptionMap: {[key: string]: string} = {
  Queued: 'queued',
  Processing: 'processing',
  Filtering: 'filtering',
  Exporting: 'exporting',
};

export interface DataSetReportStatusItem {
  status: string;
  statusLabel: string;
  errorLabel: string;
  startDate?: string;
  completeDate?: string;
  failedDate?: string;
  tooltipInfo?: string;
  stepProcessingStatus: STEP_PROCESSING_STATUS;
}

interface Props {
  progressSteps: DataSetReportStatusItem[];
}

const AurPizzaTrackerProgress: React.FC<Props> = ({progressSteps}) => {
  const topContainerRef = useRef<HTMLDivElement | null>(null);

  const windowSize = useWindowSize();

  const {progressBarWidth, pulseSize} = useMemo(() => {
    let defaultWidth = 152;
    let defaultPulseSize = 70;
    if (windowSize.width >= 1024 && windowSize.width <= 1280) {
      defaultWidth = 92;
      defaultPulseSize = 64;
    } else if (windowSize.width < 1024) {
      defaultWidth = 78;
      defaultPulseSize = 52;
    }
    return {progressBarWidth: defaultWidth, pulseSize: defaultPulseSize};
  }, [windowSize]);

  const getProressStepAriaLabel = (item: DataSetReportStatusItem) => {
    let label = null;
    let date = null;

    switch (item.stepProcessingStatus) {
      case 'Completed':
        label = ` ${t('common.pizzaTracker.on')}`;
        date = item.completeDate;
        break;
      case 'InProgress':
        label = `. ${t('common.pizzaTracker.startedOn')}`;
        date = item.startDate;
        break;
      case 'Failed':
        if ((item.errorLabel ?? '') === '') {
          label = ` ${
            item.errorLabel !== ''
              ? item.errorLabel
              : t('common.pizzaTracker.on')
          }`;
          date = item.failedDate;
        }
        break;
      default:
        break;
    }

    if (date && label) {
      return t(label) + ` ${getUTCDateTimeForDisplay(date)}`;
    }
    return label ? t(label) : null;
  };
  const renderBottomCotent = (
    caption: string,
    date?: string,
    isError?: boolean,
    stepStatus?: string
  ) => {
    const showDateTimeSeprate = windowSize.width <= 1024;
    return (
      <>
        {(isError || date) && (
          <Typography
            aria-label={t(caption)}
            className={`typo-text label2_size date-text ${isError ? ' state-text-error' : ''}`}
            data-testid={`date-caption-${stepStatus}`}
          >
            {t(caption)}
          </Typography>
        )}
        {date && (
          <AurLightTooltip title={t('common.yearMonthDate')}>
            <Typography
              aria-label={`${t(caption)} ${showDateTimeSeprate ? getUTCDateTimeForDisplay(date, ONLY_DATE_SHOW_FORMAT) : getUTCDateTimeForDisplay(date)}`}
              className={`typo-text label2_size date-text ${isError ? ' state-text-error' : ''}`}
              data-testid={`full-date-${stepStatus}`}
            >
              {showDateTimeSeprate
                ? getUTCDateTimeForDisplay(date, ONLY_DATE_SHOW_FORMAT)
                : getUTCDateTimeForDisplay(date)}
            </Typography>
          </AurLightTooltip>
        )}
        {date && showDateTimeSeprate && (
          <AurLightTooltip title={t('common.time')}>
            <Typography
              aria-label={`${t(caption)} ${getUTCDateTimeForDisplay(date, ONLY_TIME_SHOW_FORMAT)}`}
              className={`typo-text label2_size date-text ${isError ? ' state-text-error' : ''}`}
              data-testid={`only-time-date-${stepStatus}`}
            >
              {getUTCDateTimeForDisplay(date, ONLY_TIME_SHOW_FORMAT)}
            </Typography>
          </AurLightTooltip>
        )}
      </>
    );
  };

  return (
    <div className="main-container" aria-label={t('aria.processingChart')}>
      {/** Top Animated Progress Step  */}
      <div
        className="top-progress-container"
        style={{
          paddingLeft: `${progressBarWidth / 2}px`,
          paddingRight: `${progressBarWidth / 2}px`,
        }}
        ref={topContainerRef}
      >
        {progressSteps.map((item, index) => {
          return (
            <AnimatedProgressSingleStep
              isLastStep={index === progressSteps.length - 1}
              stepNo={(index + 1).toString()}
              key={item.status + '-progress'}
              status={item.status}
              progressBarWidth={progressBarWidth}
              pulseSize={pulseSize}
              stepProcessingStatus={item.stepProcessingStatus}
              statusLabel={item.statusLabel}
              errorLabel={item.errorLabel}
              bottomContentAriaLabel={getProressStepAriaLabel(item)}
            />
          );
        })}
      </div>
      {/** Bottom Step's info content */}
      <div
        className="bottom-step-text-container"
        style={{
          width: `${(pulseSize + progressBarWidth) * progressSteps.length}px`,
        }}
        aria-label={t(
          'reports.processingSummary.pizzaTrackerProgress.datasetprocessingStateCaptions'
        )}
      >
        {progressSteps.map((item) => {
          const inProgress = item.stepProcessingStatus === 'InProgress';
          const isCompleted = item.stepProcessingStatus === 'Completed';
          const isError = item.stepProcessingStatus === 'Failed';

          return (
            <div
              key={'status_' + item.status}
              className="step-content-container"
            >
              <div
                className={`status-text-row ${isError ? ' status-text-row-error' : ''}`}
              >
                <Typography
                  className={`typo-text subhead4_size text ${isError ? ' state-text-error' : ''}`}
                >
                  {item.statusLabel}
                </Typography>
                {item.tooltipInfo ? (
                  <AurTooltip
                    title={item.tooltipInfo}
                    aria-label={item.tooltipInfo}
                    tabIndex={0}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -7],
                            },
                          },
                        ],
                      },
                    }}
                    trigger="click"
                  >
                    <InfoIcon />
                  </AurTooltip>
                ) : null}
              </div>

              {isError &&
                renderBottomCotent(
                  item.errorLabel !== ''
                    ? item.errorLabel
                    : 'reports.processingSummary.pizzaTrackerProgress.failedOn',
                  item.failedDate,
                  true,
                  item.status
                )}

              {inProgress &&
                renderBottomCotent(
                  'common.pizzaTracker.startedOn',
                  item.startDate,
                  undefined,
                  item.status
                )}
              {isCompleted &&
                renderBottomCotent(
                  'common.pizzaTracker.completedOn',
                  item.completeDate,
                  undefined,
                  item.status
                )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AurPizzaTrackerProgress;
